import { createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from './store'
import { SubmitHandler } from '../api/apiHandler'
import { IFormState, createFormSlice, makeFormSelectors } from 'optimized-react-component-library-xyz123'
import questionsJson from '../content/content.questions.json'

const {
    applicationContent: appContentFromJson,
    steps: stepsFromJson,
    questions: questionsFromJson,
} = questionsJson as {
    applicationContent: any
    steps: any[]
    questions: any[]
}

const initialState: IFormState = {
    formStatus: 'idle',
    formLoaded: true,
    applicationContent: appContentFromJson,
    steps: stepsFromJson,
    questions: questionsFromJson.map(q => ({
        ...q,
        answer: '',
        isDisplayed: false,
    })),
}

/**
 * Skickar in ett nytt ärende till backend-API:t.
 * Tar emot formaterad submit-data och en endpoint-url.
 * Returnerar API-svaret från servern.
 */
export const submitApplication = createAsyncThunk(
    'Submit data',
    async ({ submitData, url }: { submitData: any; url: string }) => {
        const response = await SubmitHandler({ submitData, url })
        return response
    }
)

export const formSlice = createFormSlice(initialState, {
    name: 'Form',
    extraReducers: (builder: any) => {
        builder
            .addCase(submitApplication.pending, (state: any) => {
                state.formStatus = 'loading'
            })
            .addCase(submitApplication.fulfilled, (state: any, action: any) => {
                state.formStatus = 'success'
            })
            .addCase(submitApplication.rejected, (state: any, action: any) => {
                state.formStatus = 'failed'
            })
    },
})

export const {
    updateAnswer,
    removeAllAnswers,
    addFiles,
    resetDependentAnswersForward,
    clearDisplayedFromStep,
    setQuestionDisplayed,
    setApplicationContent,
    setStep,
    setQuestion,
} = formSlice.actions

export default formSlice.reducer

const selectors = makeFormSelectors<RootState>((state: any) => state.questions)

/**
 * Hämtar hela formulärets form-state, inklusive applicationContent,
 * steps, questions och formStatus.
 */
export const selectStateQuestion = selectors.selectFormState
/**
 * Selector som returnerar språkfiltrerat applicationContent baserat på det
 * aktuellt aktiverade språket. Används för att ladda rätt texter från
 * languageSupport innan de skrivs in i global state.
 */
export const selectApplicationContentFromLanguageSupport = selectors.selectApplicationContentFromLanguageSupport
/**
 * Selector som returnerar språkfiltrerade stegobjekt (steps) baserat på
 * det aktuellt aktiverade språket. Används för att ladda rätt steginformation
 * från languageSupport innan de skrivs in i global state.
 */
export const selectStepFromLanguageSupport = selectors.selectStepFromLanguageSupport
/**
 * Selector som returnerar språkfiltrerade frågeobjekt (questions) baserat på
 * det aktuellt aktiverade språket. Ger rätt frågetexter, val och beskrivningar
 * etc. innan de skrivs in i global state.
 */
export const selectQuestionFromLanguageSupport = selectors.selectQuestionFromLanguageSupport
/**
 * Selector som returnerar true när alla språkberoende delar av formuläret
 * (applicationContent, steps och questions) har laddats och är redo att användas.
 */
export const selectIsFormLoaded = selectors.selectIsFormLoaded
